import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Input,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from 'reactstrap';
import { useUrlSearchParams } from 'use-url-search-params';
import { DateRangePicker } from 'rsuite';
import moment from 'moment';
import { STORES } from '../../constants/showRoomCodes';

const STATUS_LABELS = {
  purifier: {
    1: 'Pending',
    11: 'New',
    12: 'Assigned To Technician',
    14: 'In Progress',
    15: 'Resolved',
    2: 'Canceled',
    13: 'Rescheduled',
  },
  cswishlist: { 1: 'Contacted', 0: 'Not Contacted' },
  advanceposting: { 1: 'Approved', 2: 'Rejected', 0: 'New' },
  bankrefund: {
    9: 'Finance Approval Pending',
    10: 'Finance Manager Approval Pending',
    20: 'Audit Approval Pending',
    3: 'Completed',
    5: 'Rejected',
  },
};

const getStatusLabel = (statusCode, type) => {
  if (
    statusCode === undefined ||
    statusCode === null ||
    statusCode === '' ||
    statusCode === 'All'
  ) {
    return '';
  }
  return STATUS_LABELS[type]?.[statusCode] ?? statusCode;
};

const FilterSearchQueue = (props) => {
  const {
    setSearchFilter,
    setStatusFilter,
    setAdvancedFilter,
    type,
    cntry,
    setStore,
    store,
    storeType,
    setStoreType,
    statusList,
    setEmirate,
    emirate,
    setArea,
    area,
    areaOptions,
    setIsAmc,
    isAmc,
    serviceType,
    setServiceType,
    serviceTypeOptions,
    technician,
    setTechnician,
    technicianOptions,
    feedbackFilter,
    setFeedbackFilter,
    feedbackCounts,
    // Optional: when both props are provided, the Filter drawer is controlled
    // by the parent and the inline Filter button is hidden.
    externalFilterDrawerOpen,
    setExternalFilterDrawerOpen,
    // Optional JSX rendered at the far-right end of the toolbar row
    // (e.g. page-level action buttons that should sit next to the filters).
    headerActions,
  } = props;

  // Feedback filter options. Counts come from the list API and describe the current
  // result set ignoring the feedback filter itself, so each option shows what it
  // would return. They are omitted from the label until the API supplies them.
  const withCount = (label, count) =>
    Number.isFinite(Number(count)) && count !== undefined && count !== null
      ? `${label} (${count})`
      : label;
  const FEEDBACK_OPTIONS = [
    { value: 'All', label: withCount('All', feedbackCounts?.all) },
    {
      value: 'with',
      label: withCount('Feedback Received', feedbackCounts?.with_feedback),
    },
    {
      value: 'without',
      label: withCount('No Feedback', feedbackCounts?.without_feedback),
    },
    ...[5, 4, 3, 2, 1].map((rating) => ({
      value: String(rating),
      label: withCount(
        `${rating} Star${rating > 1 ? 's' : ''}`,
        feedbackCounts?.ratings?.[rating],
      ),
    })),
  ];
  const feedbackChipLabel = (value) =>
    ({ with: 'Feedback Received', without: 'No Feedback' })[value] ||
    `${value} Star${Number(value) > 1 ? 's' : ''}`;

  const isFilterControlledExternally =
    typeof externalFilterDrawerOpen === 'boolean' &&
    typeof setExternalFilterDrawerOpen === 'function';

  //Advance Filter Data
  const [isActiveSearch, setIsActiveSearch] = useState(false);
  const [dateRangeValue, setDateRangeValue] = useState(null);
  const [internalFilterDrawerOpen, setInternalFilterDrawerOpen] =
    useState(false);
  const isFilterDrawerOpen = isFilterControlledExternally
    ? externalFilterDrawerOpen
    : internalFilterDrawerOpen;
  const setIsFilterDrawerOpen = isFilterControlledExternally
    ? setExternalFilterDrawerOpen
    : setInternalFilterDrawerOpen;

  // Buffered advanced-filter state — committed to parent only on Apply.
  const [draftIsAmc, setDraftIsAmc] = useState(isAmc ?? 'All');
  const [draftEmirate, setDraftEmirate] = useState(emirate ?? '');
  const [draftArea, setDraftArea] = useState(area ?? '');
  const [draftServiceType, setDraftServiceType] = useState(serviceType ?? '');
  const [draftTechnician, setDraftTechnician] = useState(technician ?? '');
  const [draftFeedback, setDraftFeedback] = useState(feedbackFilter ?? 'All');
  const [draftStore, setDraftStore] = useState(store ?? '');
  const [draftStoreType, setDraftStoreType] = useState(storeType ?? 'all');
  const [draftDateRange, setDraftDateRange] = useState(null);
  const [draftDateFilter, setDraftDateFilter] = useState({
    frmDt: '',
    toDt: '',
  });
  // Which date column the date-range filters on: created_at vs scheduled_date.
  const [draftDateType, setDraftDateType] = useState('created');

  const toggleFilterDrawer = () => {
    setIsFilterDrawerOpen((prev) => {
      const next = !prev;
      if (next) {
        // Re-sync drafts from latest committed values when opening.
        setDraftIsAmc(isAmc ?? 'All');
        setDraftEmirate(emirate ?? '');
        setDraftArea(area ?? '');
        setDraftServiceType(serviceType ?? '');
        setDraftTechnician(technician ?? '');
        setDraftFeedback(feedbackFilter ?? 'All');
        setDraftStore(store ?? '');
        setDraftStoreType(storeType ?? 'all');
        setDraftDateRange(dateRangeValue);
        setDraftDateFilter({
          frmDt: advanceFilterData?.frmDt ?? '',
          toDt: advanceFilterData?.toDt ?? '',
        });
        setDraftDateType(advanceFilterData?.dateType ?? 'created');
      }
      return next;
    });
  };

  const handleApplyFilters = () => {
    if (setIsAmc) setIsAmc(draftIsAmc);
    if (setEmirate) setEmirate(draftEmirate);
    if (setArea) setArea(draftArea);
    if (setServiceType) setServiceType(draftServiceType);
    if (setTechnician) setTechnician(draftTechnician);
    if (setFeedbackFilter) setFeedbackFilter(draftFeedback);
    if (setStore) setStore(draftStore);
    if (setStoreType) setStoreType(draftStoreType);
    const nextAdvance = {
      ...advanceFilterData,
      frmDt: draftDateFilter.frmDt,
      toDt: draftDateFilter.toDt,
      dateType: draftDateType,
    };
    setAdvanceFilterData(nextAdvance);
    setDateRangeValue(draftDateRange);
    setAdvancedFilter(nextAdvance);
    setStsSearchParams({ status });
    setStatusFilter({ status });
    setIsActiveSearch(true);
    setIsFilterDrawerOpen(false);
  };

  const handleDraftDateChange = (dates) => {
    if (dates?.[0] && dates?.[1]) {
      setDraftDateFilter({
        frmDt: moment(dates[0]).format('YYYY-MM-DD'),
        toDt: moment(dates[1]).format('YYYY-MM-DD'),
      });
      setDraftDateRange(dates);
    }
  };
  const handleDraftDateClose = () => {
    setDraftDateFilter({ frmDt: '', toDt: '' });
    setDraftDateRange(null);
  };
  //End Advance Filter Data
  const [stsSearchParams, setStsSearchParams] = useUrlSearchParams({
    status: 'All',
  });
  const [searchParams, setSearchParams] = useUrlSearchParams({ search: '' });
  const [searchValue, setSearchValue] = useState(searchParams.search || '');
  const [status, setStatus] = useState(stsSearchParams.status || 'All');
  const [advanceFilterData, setAdvanceFilterData] = useState({});

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
  };
  const handleStatus = (e) => {
    setStatus(e.target.value);
  };
  const handleEmirate = (e) => {
    setEmirate(e.target.value);
  };
  const handleArea = (e) => {
    setArea(e.target.value);
  };

  const disableFutureDates = (date) => {
    // Disables dates after today
    return date.getTime() > new Date().getTime();
  };

  const handleDateChange = (dates) => {
    if (dates?.[0] && dates?.[1]) {
      const startDate = moment(dates[0]).format('YYYY-MM-DD');
      const endDate = moment(dates[1]).format('YYYY-MM-DD');
      setAdvanceFilterData({
        ...advanceFilterData,
        frmDt: startDate,
        toDt: endDate,
      });
      setDateRangeValue(dates);
    }
  };
  const handleDateClose = () => {
    setAdvanceFilterData({
      ...advanceFilterData,
      frmDt: '',
      toDt: '',
    });
    setDateRangeValue(null);
  };
  // Set filters only on search button click
  const handleSearchButtonClick = () => {
    setIsActiveSearch(true);
    setAdvancedFilter(advanceFilterData);
    setSearchParams({ search: searchValue });
    setStsSearchParams({ status });
    setSearchFilter({ search: searchValue });
    setStatusFilter({ status });
  };

  const handleResetSearch = () => {
    setIsActiveSearch(false);
    setSearchParams({ search: '' });
    setStsSearchParams({ status: 'All' });
    setSearchFilter({ search: '' });
    setStatusFilter({ status: 'All' });
    setSearchValue('');
    setStatus('All');
    setAdvancedFilter({});
    setAdvanceFilterData({});
    setDateRangeValue(null);
    setDraftDateType('created');
    if (setStore) setStore('');
    if (setStoreType) setStoreType('all');
    if (setEmirate) setEmirate('');
    if (setArea) setArea('');
    if (setIsAmc) setIsAmc('All');
    if (setServiceType) setServiceType('');
    if (setTechnician) setTechnician('');
    if (setFeedbackFilter) setFeedbackFilter('All');
    setDraftFeedback('All');
  };

  return (
    <React.Fragment>
      <div
        className="p-3 bg-soft-light border-1 border-light"
        style={{ borderStyle: 'dashed' }}
      >
        <Row>
          <Col
            md={12}
            className="d-flex flex-row flex-wrap align-items-center gap-2"
          >
            <div className="position-relative" style={{ minWidth: 200 }}>
              <Input
                list="datalistOptions"
                id="exampleDataList"
                placeholder="Type to search..."
                type="text"
                className="form-control"
                onChange={handleSearch}
                value={searchValue}
              ></Input>
              <span className="p-2 text-light position-absolute top-50 end-0 translate-middle-y">
                <i className="bx bx-search-alt font-size-16 align-middle"></i>
              </span>
            </div>
            {type !== 'donations' && type !== 'purifier-orders' && (
              <div style={{ minWidth: 150 }}>
                <select
                  className="form-select"
                  aria-label="Default select example"
                  onChange={handleStatus}
                  value={status}
                >
                  <option value="All">Select Status</option>
                  <option value="All">All</option>
                  {type === 'cswishlist' ? (
                    <>
                      <option value="1">Contacted</option>
                      <option value="0">Not Contacted</option>
                    </>
                  ) : type === 'return' ? (
                    <>
                      <option value="Pending">Pending</option>
                      <option value="Authorized">Authorized</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Approved">Approved</option>
                      <option value="Received">Received</option>
                    </>
                  ) : type === 'wallet' ? (
                    <>
                      <option value="Active">Active</option>
                      <option value="InActive">Inactive</option>
                      <option value="Blocked">Blocked</option>
                    </>
                  ) : type === 'purifier' ? (
                    <>
                      <option value="1">Pending</option>
                      <option value="11">New</option>
                      <option value="12">Assigned To Technician</option>
                      <option value="14">In Progress</option>
                      <option value="15">Resolved</option>
                      <option value="2">Canceled</option>
                      <option value="13">Rescheduled</option>
                      <option value="3">Closed</option>
                    </>
                  ) : type === 'donations' ? (
                    <></>
                  ) : type === 'advanceposting' ? (
                    <>
                      <option value="1">Approved</option>
                      <option value="2">Rejected</option>
                      <option value="0">New</option>
                    </>
                  ) : type === 'bankrefund' ? (
                    <>
                      <option value="9">Finance Approval Pending</option>
                      <option value="10">Finance Manager Approval Pending</option>
                      <option value="20">Audit Approval Pending</option>
                      <option value="3">Completed</option>
                      <option value="5">Rejected</option>
                    </>
                  ) : statusList?.length > 0 ? (
                    <>
                      {statusList?.map((item) => {
                        // Accepts plain strings or { value, label } entries.
                        const optionValue =
                          typeof item === 'string' ? item : item?.value;
                        const optionLabel =
                          typeof item === 'string' ? item : item?.label;

                        return (
                          <option
                            className="text-capitalize"
                            key={optionValue}
                            value={optionValue}
                          >
                            {optionLabel}
                          </option>
                        );
                      })}
                    </>
                  ) : null}
                </select>
              </div>
            )}
            {!isFilterControlledExternally && (
              <Button
                outline
                color="dark"
                type="button"
                onClick={toggleFilterDrawer}
                className="d-inline-flex align-items-center gap-1"
              >
                Filter <i className="bx bx-filter-alt"></i>
              </Button>
            )}
            <div className="d-flex gap-2 flex-shrink-0">
              <Button
                color="secondary"
                type="button"
                id="srchBtn"
                onClick={handleSearchButtonClick}
              >
                <i className="bx bx-search-alt font-size-16 align-middle"></i>
              </Button>
              <Button
                outline
                color="danger"
                type="button"
                onClick={handleResetSearch}
              >
                <i className="bx bx-revision font-size-16 align-middle"></i>
              </Button>
            </div>
            {headerActions && (
              <div className="d-flex align-items-center flex-wrap gap-2 ms-auto">
                {headerActions}
              </div>
            )}
          </Col>
        </Row>
        <Row>
          <Col className="mt-2">
            <p className="m-0 d-flex gap-2 align-items-center">
              {isActiveSearch &&
              (searchParams?.search ||
                (stsSearchParams?.status && stsSearchParams?.status != 'All') ||
                (advanceFilterData && advanceFilterData?.frmDt) ||
                area ||
                isAmc ||
                emirate ||
                serviceType ||
                technician ||
                (feedbackFilter && feedbackFilter !== 'All')) ? (
                <span className="fs-11">Showing Result for : </span>
              ) : null}

              {searchParams?.search ? (
                <span className="border px-1 bg-white rounded fs-10">
                  Keyword : {searchParams?.search}{' '}
                  <i
                    className="mdi mdi-close"
                    onClick={() => {
                      setSearchFilter({ search: '' });
                      setSearchParams({ search: '' });
                      setSearchValue('');
                    }}
                  />
                </span>
              ) : null}
              {stsSearchParams?.status && stsSearchParams?.status != 'All' ? (
                <span className="border px-1 bg-white rounded fs-10">
                  Status : {getStatusLabel(stsSearchParams?.status, type)}{' '}
                  <i
                    className="mdi mdi-close"
                    onClick={() => {
                      setStatusFilter({ status: 'All' });
                      setStsSearchParams({ status: 'All' });
                      setStatus('All');
                    }}
                  />
                </span>
              ) : null}
              {advanceFilterData && advanceFilterData?.frmDt ? (
                <span className="border px-1 bg-white rounded fs-10">
                  Date From : {advanceFilterData?.frmDt}
                  {' to '} {advanceFilterData?.toDt}{' '}
                  <i
                    className="mdi mdi-close"
                    onClick={() => {
                      setAdvancedFilter((advanceFilterData) => ({
                        ...advanceFilterData,
                        frmDt: '',
                        toDt: '',
                      }));
                      setAdvanceFilterData((advanceFilterData) => ({
                        ...advanceFilterData,
                        frmDt: '',
                        toDt: '',
                      }));
                      handleDateClose();
                      // setFormattedStartDate([]);
                      // setFormattedEndDate([]);
                    }}
                  />
                </span>
              ) : null}
              {isAmc && isAmc !== 'All' && (
                <span className="border px-1 bg-white rounded fs-10">
                  AMC Status : {isAmc === '1' ? 'Active AMC' : 'Non-AMC'}{' '}
                  <i
                    className="mdi mdi-close"
                    onClick={() => {
                      if (setIsAmc) setIsAmc('All');
                    }}
                  />
                </span>
              )}
              {emirate && (
                <span className="border px-1 bg-white rounded fs-10">
                  Emirate : {emirate}{' '}
                  <i
                    className="mdi mdi-close"
                    onClick={() => {
                      setEmirate('');
                    }}
                  />
                </span>
              )}
              {area && (
                <span className="border px-1 bg-white rounded fs-10">
                  Area : {area}{' '}
                  <i
                    className="mdi mdi-close"
                    onClick={() => {
                      setArea('');
                    }}
                  />
                </span>
              )}
              {serviceType && setServiceType && (
                <span className="border px-1 bg-white rounded fs-10 text-capitalize">
                  Service Type : {String(serviceType).replace(/_/g, ' ')}{' '}
                  <i
                    className="mdi mdi-close"
                    onClick={() => setServiceType('')}
                  />
                </span>
              )}
              {technician && setTechnician && (
                <span className="border px-1 bg-white rounded fs-10">
                  Technician :{' '}
                  {technicianOptions?.find(
                    (opt) => String(opt.value) === String(technician),
                  )?.label || technician}{' '}
                  <i
                    className="mdi mdi-close"
                    onClick={() => setTechnician('')}
                  />
                </span>
              )}
              {feedbackFilter && feedbackFilter !== 'All' && setFeedbackFilter && (
                <span className="border px-1 bg-white rounded fs-10">
                  Feedback : {feedbackChipLabel(feedbackFilter)}{' '}
                  <i
                    className="mdi mdi-close"
                    onClick={() => setFeedbackFilter('All')}
                  />
                </span>
              )}
            </p>
          </Col>
        </Row>
      </div>

      <Offcanvas
        isOpen={isFilterDrawerOpen}
        toggle={toggleFilterDrawer}
        direction="end"
        style={{ width: '560px' }}
      >
        <OffcanvasHeader toggle={toggleFilterDrawer} className="border-bottom">
          Advanced Filters
        </OffcanvasHeader>
        <OffcanvasBody>
          <Row>
            {type === 'purifier' && (
              <Col md={12} className="mb-3">
                <Row>
                  <Col md={6} className="mb-3 mb-md-0">
                    <label className="form-label fw-semibold d-block">
                      Date Range
                    </label>
                    <DateRangePicker
                      size="md"
                      className="w-100"
                      placement="auto"
                      placeholder="Select a Date Range"
                      showOneCalendar={false}
                      disabledDate={disableFutureDates}
                      onChange={handleDraftDateChange}
                      onClean={handleDraftDateClose}
                      value={draftDateRange}
                    />
                  </Col>
                  <Col md={6}>
                    <label className="form-label fw-semibold d-block">
                      Date Type
                    </label>
                    <div className="d-flex gap-3 mt-1">
                      <div className="form-check">
                        <Input
                          type="radio"
                          className="form-check-input"
                          id="dateTypeCreated"
                          name="dateType"
                          value="created"
                          checked={draftDateType === 'created'}
                          onChange={(e) => setDraftDateType(e.target.value)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="dateTypeCreated"
                        >
                          Created Date
                        </label>
                      </div>
                      <div className="form-check">
                        <Input
                          type="radio"
                          className="form-check-input"
                          id="dateTypeScheduled"
                          name="dateType"
                          value="scheduled"
                          checked={draftDateType === 'scheduled'}
                          onChange={(e) => setDraftDateType(e.target.value)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="dateTypeScheduled"
                        >
                          Scheduled Date
                        </label>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>
            )}
            {['purifier', 'purifier-orders'].includes(type) && (
              <Col md={6} className="mb-3">
                <label className="form-label fw-semibold">AMC Status</label>
                <select
                  className="form-control form-select"
                  name="isAmc"
                  value={draftIsAmc}
                  onChange={(e) => setDraftIsAmc(e.target.value)}
                >
                  <option value="All">Select AMC Status</option>
                  <option value="1">Active AMC</option>
                  <option value="0">Non-AMC</option>
                </select>
              </Col>
            )}
            {['purifier', 'purifier-orders'].includes(type) && (
              <Col md={6} className="mb-3">
                <label className="form-label fw-semibold">Emirate</label>
                <select
                  className="form-control form-select"
                  name="emirate"
                  value={draftEmirate}
                  onChange={(e) => setDraftEmirate(e.target.value)}
                >
                  <option value="">All Emirates</option>
                  <option value="Dubai">Dubai</option>
                  <option value="Abu Dhabi">Abu Dhabi</option>
                  <option value="Sharjah">Sharjah</option>
                  <option value="Ajman">Ajman</option>
                  <option value="Fujairah">Fujairah</option>
                  <option value="Ras Al Khaimah">Ras Al Khaimah</option>
                  <option value="Umm Al Quwain">Umm Al Quwain</option>
                </select>
              </Col>
            )}
            {setServiceType &&
              Array.isArray(serviceTypeOptions) &&
              serviceTypeOptions.length > 0 && (
                <Col md={6} className="mb-3">
                  <label className="form-label fw-semibold">Service Type</label>
                  <select
                    className="form-control form-select text-capitalize"
                    name="serviceType"
                    value={draftServiceType}
                    onChange={(e) => setDraftServiceType(e.target.value)}
                  >
                    <option value="">All Service Types</option>
                    {serviceTypeOptions.map((opt) => {
                      const value = typeof opt === 'string' ? opt : opt.value;
                      const label =
                        typeof opt === 'string'
                          ? opt.replace(/_/g, ' ')
                          : opt.label;
                      return (
                        <option
                          key={value}
                          value={value}
                          className="text-capitalize"
                        >
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </Col>
              )}
            {setTechnician &&
              Array.isArray(technicianOptions) &&
              technicianOptions.length > 0 && (
                <Col md={6} className="mb-3">
                  <label className="form-label fw-semibold">Technician</label>
                  <select
                    className="form-control form-select"
                    name="technician"
                    value={draftTechnician}
                    onChange={(e) => setDraftTechnician(e.target.value)}
                  >
                    <option value="">All Technicians</option>
                    {technicianOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </Col>
              )}
            {setFeedbackFilter && (
              <Col md={6} className="mb-3">
                <label className="form-label fw-semibold">
                  Customer Feedback
                </label>
                <select
                  className="form-control form-select"
                  name="feedbackFilter"
                  value={draftFeedback}
                  onChange={(e) => setDraftFeedback(e.target.value)}
                >
                  {FEEDBACK_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Col>
            )}
            {['purifier', 'purifier-orders'].includes(type) && areaOptions && (
              <Col md={6} className="mb-3">
                <label className="form-label fw-semibold">Area</label>
                <select
                  className="form-control form-select"
                  name="area"
                  value={draftArea}
                  onChange={(e) => setDraftArea(e.target.value)}
                >
                  <option value="">All Areas</option>
                  {(areaOptions || []).map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </Col>
            )}
            {setStore && (
              <Col md={6} className="mb-3">
                <label className="form-label fw-semibold">Store</label>
                <select
                  className="form-control form-select"
                  name="store"
                  value={draftStore}
                  onChange={(e) => setDraftStore(e?.target?.value)}
                >
                  <option value="">All Stores</option>
                  {(() => {
                    if (!cntry || cntry === 'all') {
                      return Object.entries(STORES)
                        .flatMap(([, stores]) => Object.entries(stores))
                        .map(([key, value]) => (
                          <option key={key} value={key}>
                            {value}
                          </option>
                        ));
                    } else if (STORES[cntry]) {
                      return Object.entries(STORES[cntry]).map(
                        ([key, value]) => (
                          <option key={key} value={key}>
                            {value}
                          </option>
                        ),
                      );
                    }
                    return null;
                  })()}
                </select>
              </Col>
            )}
            {setStoreType && (
              <Col md={6} className="mb-3">
                <label className="form-label fw-semibold">Store Type</label>
                <select
                  className="form-control form-select"
                  name="storeType"
                  value={draftStoreType}
                  onChange={(e) => setDraftStoreType(e?.target?.value)}
                >
                  <option value="all">All Types</option>
                  <option value="store-return">Store</option>
                  <option value="online-return">Online</option>
                  <option value="b2b">B2B Orders</option>
                </select>
              </Col>
            )}
            {type !== 'purifier' && (
              <Col md={6} className="mb-3">
                <label className="form-label fw-semibold">Date Range</label>
                <DateRangePicker
                  size="md"
                  className="w-100"
                  placement="auto"
                  placeholder="Select a Date Range"
                  showOneCalendar={false}
                  disabledDate={disableFutureDates}
                  onChange={handleDraftDateChange}
                  onClean={handleDraftDateClose}
                  value={draftDateRange}
                />
              </Col>
            )}
          </Row>

          <div className="d-flex gap-2 mt-4 justify-content-start">
            <Button color="success" onClick={handleApplyFilters}>
              Apply
            </Button>
            <Button outline color="danger" onClick={handleResetSearch}>
              Reset
            </Button>
          </div>
        </OffcanvasBody>
      </Offcanvas>
    </React.Fragment>
  );
};

export default FilterSearchQueue;
